import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ScrollText, Check } from "lucide-react";
import { useUser } from "../login/UserContext";
import { acceptCurrentTerms } from "./legal-service";
import {
  BRAND,
  PRIVACY_PATH,
  TERMS_EFFECTIVE,
  TERMS_PATH,
  TERMS_VERSION,
} from "./legal-config";
import "./legal.css";

/**
 * The "we've updated our Terms" prompt.
 *
 * New accounts agree at sign-up — the sign-in modal is clickwrap and the API
 * stamps the current version on creation — so this only ever surfaces for
 * members who predate a version bump. It's a sheet rather than a hard wall:
 * agreeing is one tap, "Not now" steps aside for the session, and the legal
 * pages open in a new tab so reading them doesn't lose your place.
 *
 * Kept out of the way on the legal pages themselves, and after a successful
 * acceptance it confirms briefly before leaving.
 */

const SNOOZE_KEY = "bb_terms_prompt_snoozed";

const TermsGate: React.FC = () => {
  const { user, setUser, authReady } = useUser();
  const { pathname } = useLocation();

  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const isAuthed = authReady && !!user && user.id > 0;
  const outstanding = isAuthed && user.terms_accepted_version !== TERMS_VERSION;
  const onLegalPage = pathname === TERMS_PATH || pathname === PRIVACY_PATH;

  // Let the app paint first — a legal sheet racing the first render reads as a
  // popup, not as a considered notice.
  useEffect(() => {
    // Agreeing marks the account current, which lands here as "nothing
    // outstanding" and would yank the sheet mid-thanks. The acceptance path
    // owns the dismissal from that point; leave it alone.
    if (done) return;
    if (!outstanding || onLegalPage) {
      setVisible(false);
      return;
    }
    let snoozed = false;
    try {
      snoozed = sessionStorage.getItem(SNOOZE_KEY) === TERMS_VERSION;
    } catch {
      /* private mode — show it */
    }
    if (snoozed) return;
    const timer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(timer);
  }, [outstanding, onLegalPage, done]);

  const snooze = () => {
    try {
      sessionStorage.setItem(SNOOZE_KEY, TERMS_VERSION);
    } catch {
      /* nothing to do */
    }
    setVisible(false);
  };

  const agree = async () => {
    setSaving(true);
    setError("");
    const ok = await acceptCurrentTerms();
    setSaving(false);
    if (!ok) {
      setError("We couldn't save that just now. Try again in a moment.");
      return;
    }
    setUser((prev) => ({ ...prev, terms_accepted_version: TERMS_VERSION }));
    setDone(true);
    setTimeout(() => setVisible(false), 1500);
  };

  if (!visible) return null;

  return createPortal(
    <div className="bb-terms-gate" role="dialog" aria-modal="false" aria-label="Updated Terms of Service">
      <div className="bb-terms-gate__scrim" onClick={snooze} />
      <div className="bb-terms-gate__sheet">
        {done ? (
          <div className="bb-terms-gate__done">
            <span className="bb-terms-gate__doneIcon">
              <Check size={22} strokeWidth={2.6} />
            </span>
            <p className="bb-terms-gate__doneText">Thanks — you're all set.</p>
          </div>
        ) : (
          <>
            <span className="bb-terms-gate__icon" aria-hidden="true">
              <ScrollText size={20} strokeWidth={2} />
            </span>
            <h2 className="bb-terms-gate__title">We've updated our Terms</h2>
            <p className="bb-terms-gate__body">
              {BRAND} now has a member feed, so our Terms of Service and Privacy
              Policy have been rewritten in plain English to cover it. Nothing
              about your plan or your data changed — here's the gist:
            </p>
            <ul className="bb-terms-gate__list">
              <li>We publish analysis; you place your own bets, at your own book.</li>
              <li>What you post on the feed stays yours, and you stand behind it.</li>
              <li>Plans renew until you cancel, and you can cancel yourself.</li>
            </ul>
            <p className="bb-terms-gate__links">
              Read the full{" "}
              <Link to={TERMS_PATH} target="_blank" rel="noreferrer">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to={PRIVACY_PATH} target="_blank" rel="noreferrer">
                Privacy Policy
              </Link>{" "}
              — effective {TERMS_EFFECTIVE}.
            </p>
            {error && <p className="bb-terms-gate__error">{error}</p>}
            <div className="bb-terms-gate__actions">
              <button type="button" className="bb-terms-gate__snooze" onClick={snooze}>
                Not now
              </button>
              <button
                type="button"
                className="bb-terms-gate__agree"
                onClick={agree}
                disabled={saving}
              >
                {saving ? "Saving…" : "Agree and continue"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TermsGate;
